/**
 * Cognito authentication service.
 *
 * Handles the OAuth2 authorization code flow:
 * 1. Redirect to Cognito hosted UI for login
 * 2. Cognito redirects back with an authorization code
 * 3. Exchange the code for JWT tokens
 * 4. Store tokens and attach to API calls
 */

function requireEnv(key: string): string {
  const value = import.meta.env[key]
  if (!value) {
    throw new Error(
      `Missing required environment variable: ${key}. ` +
      `Ensure it is set in .env.local (for development) or your deployment environment.`
    )
  }
  return value as string
}

const COGNITO_CONFIG = {
  get userPoolId() { return requireEnv("VITE_COGNITO_USER_POOL_ID") },
  get clientId()   { return requireEnv("VITE_COGNITO_CLIENT_ID") },
  get domain()     { return requireEnv("VITE_COGNITO_DOMAIN") },
  get identityProvider() { return requireEnv("VITE_COGNITO_IDENTITY_PROVIDER") },
  get redirectUri() { return window.location.origin + "/callback" },
  scopes: ["openid", "email", "profile"],
}

/**
 * Token storage.
 *
 * SECURITY NOTE: tokens live in `sessionStorage`, which is readable by any
 * script on the page — a successful XSS could exfiltrate them (OWASP DOM-XSS /
 * JWT cheat sheets advise against web storage for tokens). The robust
 * mitigation keeps tokens out of JS entirely, in httpOnly+Secure+SameSite
 * cookies set by a backend token-exchange endpoint. This SPA talks to the
 * Cognito hosted UI directly with no such backend, so we use `sessionStorage`
 * (preferred over `localStorage`: tab-scoped, cleared on close) and rely on the
 * PKCE + `state` protections below plus a strict CSP to reduce attack surface.
 * Revisit if a BFF is introduced.
 */
const TOKEN_KEY = "eclipse_ffp_tokens"
/** Transient PKCE/CSRF material for the in-flight authorization request. */
const PKCE_VERIFIER_KEY = "eclipse_ffp_pkce_verifier"
const OAUTH_STATE_KEY = "eclipse_ffp_oauth_state"
/** Where to send the user back after a re-auth round-trip (route preservation). */
const RETURN_TO_KEY = "eclipse_ffp_return_to"

interface TokenResponse {
  id_token: string
  access_token: string
  refresh_token?: string
  expires_in: number
  token_type: string
}

interface StoredTokens {
  idToken: string
  accessToken: string
  refreshToken?: string
  expiresAt: number
}

// ─── PKCE / CSRF helpers (RFC 7636 + RFC 6749 §10.12) ──────────────────────────────

/** Base64url-encode raw bytes (no padding, URL-safe alphabet). */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = ""
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

/** Cryptographically-random URL-safe string for `state` / PKCE verifier. */
function randomUrlSafe(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength)
  crypto.getRandomValues(bytes)
  return base64UrlEncode(bytes)
}

/** S256 PKCE challenge: base64url(SHA-256(verifier)). */
async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))
  return base64UrlEncode(new Uint8Array(digest))
}

/** Parse + expiry-check a raw stored-token string (shared by sync/async getters). */
function parseStoredTokens(raw: string | null): StoredTokens | null {
  if (!raw) return null
  try {
    const tokens: StoredTokens = JSON.parse(raw)
    // 60s clock-skew buffer.
    if (Date.now() > tokens.expiresAt - 60000) return null
    return tokens
  } catch {
    return null
  }
}

/** Get stored tokens from sessionStorage, attempting silent refresh if expired. */
export async function getStoredTokensAsync(): Promise<StoredTokens | null> {
  const raw = sessionStorage.getItem(TOKEN_KEY)
  if (!raw) return null
  // Valid (non-expired) — reuse the shared parser.
  const valid = parseStoredTokens(raw)
  if (valid) return valid
  // Expired or unparseable — try silent refresh if we have a refresh token.
  try {
    const tokens: StoredTokens = JSON.parse(raw)
    if (tokens.refreshToken) return refreshTokens(tokens.refreshToken)
  } catch {
    // fall through
  }
  sessionStorage.removeItem(TOKEN_KEY)
  return null
}

/** Synchronous token check — does NOT attempt refresh. Use for auth guards. */
export function getStoredTokens(): StoredTokens | null {
  const tokens = parseStoredTokens(sessionStorage.getItem(TOKEN_KEY))
  if (!tokens) {
    // Clear an expired/corrupt entry so stale material doesn't linger.
    if (sessionStorage.getItem(TOKEN_KEY)) sessionStorage.removeItem(TOKEN_KEY)
    return null
  }
  return tokens
}

/**
 * Exchange a refresh token for a fresh set of tokens.
 * Uses the Cognito token endpoint with grant_type=refresh_token.
 */
async function refreshTokens(refreshToken: string): Promise<StoredTokens | null> {
  try {
    const response = await fetch(`https://${COGNITO_CONFIG.domain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: COGNITO_CONFIG.clientId,
        refresh_token: refreshToken,
      }),
    })
    if (!response.ok) {
      sessionStorage.removeItem(TOKEN_KEY)
      return null
    }
    const tokenResponse: TokenResponse = await response.json()
    // Cognito does not return a new refresh_token on refresh — preserve the old one
    return storeTokens({ ...tokenResponse, refresh_token: tokenResponse.refresh_token ?? refreshToken })
  } catch {
    sessionStorage.removeItem(TOKEN_KEY)
    return null
  }
}

/** Store tokens in sessionStorage */
function storeTokens(response: TokenResponse): StoredTokens {
  const tokens: StoredTokens = {
    idToken: response.id_token,
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    expiresAt: Date.now() + response.expires_in * 1000,
  }
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
  return tokens
}

/** Get the access token for API calls (synchronous, no refresh). */
export function getAccessToken(): string | null {
  const tokens = getStoredTokens()
  return tokens?.accessToken ?? null
}

/** Get the ID token (contains user info) — synchronous, no refresh. */
export function getIdToken(): string | null {
  const tokens = getStoredTokens()
  return tokens?.idToken ?? null
}

/** Check if user is authenticated (synchronous). */
export function isAuthenticated(): boolean {
  return getStoredTokens() !== null
}

// ─── Session continuity: proactive silent refresh ──────────────────────────────

/** Read stored tokens WITHOUT the expiry check (for refresh-token access). */
function readRawTokens(): StoredTokens | null {
  const raw = sessionStorage.getItem(TOKEN_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as StoredTokens
  } catch {
    return null
  }
}

/** Refresh this far ahead of the access/ID token expiry. */
const REFRESH_LEAD_MS = 120_000
let refreshTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Schedule a silent token refresh shortly before the current token expires,
 * then reschedule off the new expiry — keeping the session alive (up to the
 * refresh token's own lifetime) with no visible interruption. Without this the
 * session ends at the short access-token TTL even though a long-lived refresh
 * token is available. Idempotent (clears any existing timer first); no-op when
 * there is no refresh token.
 */
export function scheduleTokenRefresh(): void {
  stopTokenRefresh()
  const tokens = readRawTokens()
  if (!tokens?.refreshToken) return
  const delay = Math.max(0, tokens.expiresAt - Date.now() - REFRESH_LEAD_MS)
  refreshTimer = setTimeout(() => {
    refreshTimer = null
    const current = readRawTokens()
    if (!current?.refreshToken) return
    void refreshTokens(current.refreshToken).then((refreshed) => {
      // On success, reschedule off the new expiry. On failure refreshTokens has
      // already cleared storage; the next navigation/API call routes to login.
      if (refreshed) scheduleTokenRefresh()
    })
  }, delay)
}

/** Cancel any pending silent refresh (call on logout / guard unmount). */
export function stopTokenRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

// ─── Route preservation across the login round-trip ─────────────────────────────

/** Capture the current in-app route so we can return to it after re-auth. */
function captureReturnTo(): void {
  const path = window.location.pathname + window.location.search
  // Never capture the callback route (would loop) or an empty path.
  if (path && !path.startsWith("/callback")) {
    sessionStorage.setItem(RETURN_TO_KEY, path)
  }
}

/**
 * Read and clear the post-login return route. Defaults to "/" and only allows
 * internal absolute paths (rejects protocol-relative "//host" open redirects).
 */
export function consumeReturnTo(): string {
  const raw = sessionStorage.getItem(RETURN_TO_KEY)
  sessionStorage.removeItem(RETURN_TO_KEY)
  if (raw && raw.startsWith("/") && !raw.startsWith("//")) return raw
  return "/"
}

/**
 * Redirect to the Cognito hosted UI for login.
 *
 * Uses PKCE (S256) so the single-use authorization code is useless without the
 * verifier (RFC 7636 — required for public SPA clients), and an opaque `state`
 * to defend against CSRF / code-injection (RFC 6749 §10.12). Both are stashed
 * in sessionStorage and validated on the way back in {@link handleCallback}.
 *
 * Captures the current route first so {@link consumeReturnTo} can send the user
 * back where they were after re-auth (instead of dumping them on the dashboard).
 *
 * Async because computing the S256 challenge uses the WebCrypto SubtleCrypto
 * digest. Callers that redirect-and-bail can fire-and-forget: `void redirectToLogin()`.
 */
export async function redirectToLogin(): Promise<void> {
  captureReturnTo()
  const state = randomUrlSafe()
  const verifier = randomUrlSafe()
  sessionStorage.setItem(OAUTH_STATE_KEY, state)
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier)
  const challenge = await pkceChallenge(verifier)
  const params = new URLSearchParams({
    response_type: "code",
    client_id: COGNITO_CONFIG.clientId,
    redirect_uri: COGNITO_CONFIG.redirectUri,
    scope: COGNITO_CONFIG.scopes.join(" "),
    identity_provider: COGNITO_CONFIG.identityProvider,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  })
  window.location.href = `https://${COGNITO_CONFIG.domain}/oauth2/authorize?${params}`
}

/**
 * Exchange an authorization code for tokens.
 *
 * Validates the returned `state` against the value we stored before redirecting
 * (CSRF defense) and sends the PKCE `code_verifier` to complete the S256
 * challenge. The transient state/verifier are cleared regardless of outcome.
 *
 * @param code  authorization code from the callback query string
 * @param state `state` value echoed back by Cognito (must match what we sent)
 */
export async function handleCallback(code: string, state: string | null): Promise<StoredTokens> {
  const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY)
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY)
  // One-shot material — clear immediately so a replay can't reuse it.
  sessionStorage.removeItem(OAUTH_STATE_KEY)
  sessionStorage.removeItem(PKCE_VERIFIER_KEY)

  if (!expectedState || state !== expectedState) {
    throw new Error("OAuth state mismatch — possible CSRF. Please sign in again.")
  }
  if (!verifier) {
    throw new Error("Missing PKCE verifier — please restart sign-in.")
  }

  const response = await fetch(`https://${COGNITO_CONFIG.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: COGNITO_CONFIG.clientId,
      code,
      redirect_uri: COGNITO_CONFIG.redirectUri,
      code_verifier: verifier,
    }),
  })

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status}`)
  }

  const tokenResponse: TokenResponse = await response.json()
  return storeTokens(tokenResponse)
}

/**
 * Strip the Midway federation prefix from a username.
 * Cognito returns federated identities as "Federate_<login>" — users only
 * care about the bare login (e.g. "kommanac").
 */
function stripFederatePrefix(name: string): string {
  return name.replace(/^Federate_/i, "")
}

/**
 * Parse user info from the ID token (JWT).
 *
 * Handles base64url encoding correctly: JWT uses `-` instead of `+` and
 * `_` instead of `/`, with no padding. `atob` requires standard base64.
 */
export function getUserInfo(): { email: string; name: string } | null {
  // NOTE: the JWT signature is NOT verified here — this decode is for DISPLAY
  // only (name/email). Never use these claims for authorization decisions; the
  // API Gateway Cognito authorizer is the source of truth for access control.
  const idToken = getIdToken()
  if (!idToken) return null
  try {
    const base64url = idToken.split(".")[1]
    const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, "=")
    const payload = JSON.parse(atob(padded))
    const rawName = payload.name
      ?? payload.preferred_username
      ?? payload["cognito:username"]
      ?? payload.email?.split("@")[0]
      ?? "User"
    return {
      email: payload.email ?? "",
      // Midway federation prefixes the username (e.g. "Federate_kommanac").
      // Show only the bare login (kommanac).
      name: stripFederatePrefix(rawName),
    }
  } catch {
    return null
  }
}
